/**
 * productPickerCache.ts
 *
 * Per-app-session TTL cache for the marketplace product picker.
 * Reduces API load: pages already fetched are held so name filtering
 * can run entirely client-side, and reopening the picker for the same
 * (platform, shop) within the TTL window reuses what's loaded.
 *
 * Scope:
 *   - Keyed by `(platform, shopId)`. Two platforms with the same shop
 *     id string never collide. Survives modal close/reopen.
 *   - 5-minute **sliding** TTL — every successful append refreshes
 *     `fetchedAt`, so a long active session never silently loses its
 *     cache mid-use. Stale entries are re-fetched lazily on next open.
 *   - In-memory only (resets on hard refresh / app restart).
 *
 * Refresh:
 *   `invalidate(platform, shopId)` lets the picker's refresh button
 *   drop the entry without waiting for TTL expiry.
 */

import type { Product } from "src/app/modules/product/types/ProductListing"
import type { PickerPlatform } from "./productSearchClient"

const TTL_MS = 5 * 60 * 1000 // 5 minutes

export interface PickerCacheEntry {
  /** All rows the picker has fetched for this shop in this session. */
  products: Product[]
  /** Next cursor for the discovery (no-filter) paginator; null when exhausted. */
  nextCursor: string | null
  /** Mirrors `nextCursor !== null`. */
  hasMore: boolean
  /** When the entry was last populated (Date.now() ms). */
  fetchedAt: number
}

const cacheKey = (platform: PickerPlatform, shopId: string): string =>
  `${platform}:${shopId}`

class ProductPickerCache {
  private store = new Map<string, PickerCacheEntry>()

  /** Returns the entry only if it's still within TTL; null otherwise. */
  get(platform: PickerPlatform, shopId: string): PickerCacheEntry | null {
    const key = cacheKey(platform, shopId)
    const entry = this.store.get(key)
    if (!entry) return null
    if (Date.now() - entry.fetchedAt > TTL_MS) {
      this.store.delete(key)
      return null
    }
    return entry
  }

  /** Replace the entire entry (used after a page-1 fetch). */
  set(
    platform: PickerPlatform,
    shopId: string,
    entry: Omit<PickerCacheEntry, "fetchedAt">
  ): void {
    this.store.set(cacheKey(platform, shopId), {
      ...entry,
      fetchedAt: Date.now(),
    })
  }

  /**
   * Append a page of rows (Load More). **Sliding TTL** — `fetchedAt` is
   * refreshed to `Date.now()` on every append so a long active session
   * never silently loses its cache mid-use. Deduped by product id.
   */
  append(
    platform: PickerPlatform,
    shopId: string,
    rows: Product[],
    nextCursor: string | null,
    hasMore: boolean
  ): void {
    const key = cacheKey(platform, shopId)
    const existing = this.store.get(key)
    if (!existing) {
      this.set(platform, shopId, { products: rows, nextCursor, hasMore })
      return
    }
    const seen = new Set(existing.products.map((p) => p.id))
    const fresh = rows.filter((p) => !seen.has(p.id))
    this.store.set(key, {
      ...existing,
      products: existing.products.concat(fresh),
      nextCursor,
      hasMore,
      fetchedAt: Date.now(),
    })
  }

  /** Drop the entry (refresh button). */
  invalidate(platform: PickerPlatform, shopId: string): void {
    this.store.delete(cacheKey(platform, shopId))
  }

  /** Test helper. */
  clearAll(): void {
    this.store.clear()
  }
}

const productPickerCache = new ProductPickerCache()
export default productPickerCache
